import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Flag, Sparkles, ArrowRight, Check, Clock, Target } from 'lucide-react';
import { apiClient } from '../../api/client';

/**
 * StrategicRoadmap — Sprint 172 Part 1B.
 *
 * Vertical 3-phase product-and-capital roadmap mounted at the top of the
 * Milestones tab. Live traction (live_users) feeds per-phase progress
 * bars from /api/vault/roadmap.
 *
 * Cream cards (#FAFAED) + deep-plum text per the Sprint 172 design spec.
 */

const PALETTE = {
  cream: '#FAFAED',
  plum: '#2A2235',
  muted: '#5C526A',
  coral: '#F99B7D',
  mint: '#A3E2C9',
  lavender: '#9BB8ED',
  border: 'rgba(92,82,106,0.10)',
};

const fmtMoney = (n) => {
  if (n === null || n === undefined) return '—';
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
  return `$${Number(n).toLocaleString()}`;
};

const fmtUsers = (n) => {
  if (n === null || n === undefined) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return Number(n).toLocaleString();
};

export const StrategicRoadmap = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try { setData(await apiClient.request('/api/vault/roadmap')); }
    catch (e) { setErr(e.message || 'Failed to load roadmap'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <div className="rounded-2xl p-5 text-sm" style={{ background: PALETTE.cream, border: `1px solid ${PALETTE.border}`, color: PALETTE.muted }} data-testid="roadmap-loading">
        <Loader2 size={14} className="inline animate-spin mr-2" /> Loading strategic roadmap…
      </div>
    );
  }
  if (err) {
    return (
      <div className="rounded-2xl p-5 text-sm" style={{ background: '#F2E4E4', color: '#7A4040' }} data-testid="roadmap-error">{err}</div>
    );
  }
  if (!data) return null;

  const phases = data.phases || [];
  const liveUsers = data.live_users || 0;

  return (
    <div className="rounded-2xl p-5 lg:p-6" style={{ background: PALETTE.cream, border: `1px solid ${PALETTE.border}`, borderLeft: `4px solid ${PALETTE.coral}` }} data-testid="strategic-roadmap">
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold mb-1" style={{ color: '#9A4A33' }}>Strategic Roadmap</div>
          <h3 className="text-lg font-black" style={{ color: PALETTE.plum }}>Two-step capital path · mapped to live traction</h3>
          <p className="text-xs mt-0.5" style={{ color: PALETTE.muted }}>
            Each phase progress bar moves as real users land in the platform. The dynamic valuation engine unclamps at the inflection boundary.
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: PALETTE.muted }}>Live Subscribers</div>
          <div className="text-2xl font-black tabular-nums" style={{ color: PALETTE.plum }} data-testid="roadmap-live-users">{fmtUsers(liveUsers)}</div>
          <div className="text-[10px]" style={{ color: PALETTE.muted }}>source · {data.live_users_source || 'auto'}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        <KpiTile label="Live Enterprise Valuation" value={fmtMoney(data.enterprise_valuation_usd)} sub={data.is_floor_active ? `Floor active · ${fmtMoney(data.floor_usd)}` : 'Above floor — revenue-scaled'} testid="roadmap-valuation" accent="#9A4A33" />
        <KpiTile label="Milestone Level" value={`L${data.milestone_level ?? 0}`} sub={data.milestone_label || '—'} testid="roadmap-milestone" accent="#3F4D7A" />
        <KpiTile label="Active Phase" value={(phases.find(p => p.is_current) || {}).label || '—'} sub={(phases.find(p => p.is_current) || {}).subtitle || ''} testid="roadmap-active-phase" accent="#1F5C44" />
      </div>

      <ol className="relative space-y-5" data-testid="roadmap-phases">
        {phases.map((p, idx) => {
          const isLast = idx === phases.length - 1;
          let stateLabel, stateBg, stateFg, StateIcon;
          if (p.is_complete) {
            stateLabel = 'Complete'; stateBg = PALETTE.mint; stateFg = '#1F5C44'; StateIcon = Check;
          } else if (p.is_current) {
            stateLabel = 'Current'; stateBg = PALETTE.coral; stateFg = '#fff'; StateIcon = Sparkles;
          } else {
            stateLabel = 'Upcoming'; stateBg = '#E9E4F2'; stateFg = '#5C526A'; StateIcon = Clock;
          }
          return (
            <li key={p.phase_id} className="relative pl-10" data-testid={`roadmap-phase-${p.phase_id}`}>
              {/* Vertical line */}
              {!isLast && <span aria-hidden className="absolute left-3 top-7 bottom-[-1.25rem] w-0.5" style={{ background: 'rgba(92,82,106,0.15)' }} />}
              {/* Node */}
              <span className="absolute left-0 top-1 w-6 h-6 rounded-full flex items-center justify-center font-black text-[11px]" style={{ background: stateBg, color: stateFg, border: `2px solid ${PALETTE.cream}` }} data-testid={`roadmap-node-${p.phase_id}`}>
                {p.phase}
              </span>

              <div className="rounded-xl p-4" style={{ background: '#fff', border: `1px solid ${PALETTE.border}`, borderLeft: `4px solid ${p.completion_band_color || PALETTE.coral}` }}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <Flag size={12} style={{ color: p.completion_band_color || PALETTE.coral }} />
                      <h4 className="text-base font-black" style={{ color: PALETTE.plum }}>{p.label}</h4>
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: stateBg, color: stateFg }} data-testid={`roadmap-state-${p.phase_id}`}>
                        <StateIcon size={9} /> {stateLabel}
                      </span>
                    </div>
                    <p className="text-xs mt-0.5" style={{ color: PALETTE.muted }}>{p.subtitle}</p>
                  </div>
                  {p.round && (
                    <div className="text-right flex-shrink-0">
                      <div className="text-[10px] uppercase tracking-[0.12em]" style={{ color: PALETTE.muted }}>{p.round.round_label}</div>
                      <div className="text-sm font-black" style={{ color: PALETTE.plum }} data-testid={`roadmap-round-raise-${p.phase_id}`}>{fmtMoney(p.round.target_raise_usd)}</div>
                      <div className="text-[10px]" style={{ color: PALETTE.muted }}>{p.round.basis_label}</div>
                    </div>
                  )}
                </div>

                {p.tagline && <p className="text-sm mb-3" style={{ color: PALETTE.plum }}>{p.tagline}</p>}

                {/* Progress bar */}
                <div className="mb-2">
                  <div className="flex items-center justify-between text-[10px] mb-1" style={{ color: PALETTE.muted }}>
                    <span>
                      <Target size={10} className="inline -mt-0.5 mr-1" />
                      {p.user_ceiling
                        ? `${fmtUsers(p.user_floor)} → ${fmtUsers(p.user_ceiling)} active users`
                        : `${fmtUsers(p.user_floor)}+ active users (open horizon)`}
                    </span>
                    <span className="font-bold" style={{ color: PALETTE.plum }} data-testid={`roadmap-progress-${p.phase_id}`}>{(p.progress_pct || 0).toFixed(1)}%</span>
                  </div>
                  <div className="h-2 rounded-full overflow-hidden" style={{ background: '#F0EDF5' }}>
                    <div className="h-full transition-all" style={{ width: `${Math.min(100, p.progress_pct || 0)}%`, background: p.completion_band_color || PALETTE.coral }} />
                  </div>
                  {p.users_remaining !== null && p.users_remaining !== undefined && (
                    <div className="text-[10px] mt-1 text-right" style={{ color: PALETTE.muted }} data-testid={`roadmap-remaining-${p.phase_id}`}>
                      {fmtUsers(p.users_remaining)} users to inflection
                    </div>
                  )}
                </div>

                {/* Key initiatives */}
                {(p.key_initiatives || []).length > 0 && (
                  <ul className="mt-3 space-y-1" data-testid={`roadmap-initiatives-${p.phase_id}`}>
                    {p.key_initiatives.map((ki, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-xs" style={{ color: PALETTE.plum }}>
                        <ArrowRight size={11} style={{ color: p.completion_band_color || PALETTE.coral, flexShrink: 0, marginTop: 2 }} />
                        <span>{ki}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

const KpiTile = ({ label, value, sub, testid, accent }) => (
  <div className="rounded-xl p-3" style={{ background: '#fff', border: `1px solid ${PALETTE.border}` }} data-testid={testid}>
    <div className="text-[10px] uppercase tracking-[0.14em] font-bold" style={{ color: PALETTE.muted }}>{label}</div>
    <div className="text-xl font-black tabular-nums mt-0.5" style={{ color: accent }}>{value}</div>
    {sub && <div className="text-[10px] mt-0.5" style={{ color: PALETTE.muted }}>{sub}</div>}
  </div>
);

export default StrategicRoadmap;
